/// <reference types="@types/segment-analytics" />
import { CustomerPolicy } from '@aaa-ncnu-ie/mypolicy-api-customers';
import { capitalize } from '@mui/material';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useWelcomeQuery } from '../api/getWelcomeDetails';
import { AmountFormValues } from '../pages/Payment/validation';
import { handleForwardSlash } from '../pages/PolicyDetails/helper';
import { AnalyticsAccountTypes } from '../store/paymentsReducer';
import { getClubCode, getCustomerName } from '../utils/helpers';
import useIsMobileAppUser from './useIsMobileAppUser';

declare global {
  interface Window {
    analytics?: SegmentAnalytics.AnalyticsJS;
  }
}

export const CHANNELS = {
  web: 'web',
  mobileApp: 'aaa national app',
} as const;

export type Channel = (typeof CHANNELS)[keyof typeof CHANNELS];

const getChannel = (isMobileAppUser: boolean): Channel =>
  isMobileAppUser ||
  new URLSearchParams(window.location.search).get('isMobileAppUser') === 'true'
    ? CHANNELS.mobileApp
    : CHANNELS.web;

export type EventTypes =
  | 'File Downloaded'
  | 'Link Accessed'
  | 'System Messaged'
  | 'Option Selected'
  | 'UX Modified'
  | 'Chat / Cobrowse'
  | 'User Information Entered'
  | 'Automated System Process'
  | 'Messaged';
export type SelectionTypes = string;
export type MethodTypes = 'Call' | 'Email' | 'Chat' | 'Text';
type PaymentTypes = 'Multiple Policies' | 'One Policy';
export type StatusTypes =
  | 'Failed'
  | 'Succeeded'
  | 'On'
  | 'Off'
  | 'maintenance'
  | 'national holiday'
  | 'online'
  | 'offline'
  | 'failed'
  | 'enabled'
  | 'disabled'
  | 'ended';
type LocationTypes =
  | 'Left Navigational Pane'
  | 'Go-Green Paperless Card'
  | 'Header Tab'
  | 'Feature Card'
  | 'Secondary Card'
  | 'Modal'
  | 'Banner'
  | 'Footer'
  | 'MySafeDrive Card'
  | 'SMARTtrek Card';
type MessageTypes = 'Error' | 'Warning' | 'Info' | 'Confirmation' | 'Default';
type EvalueStatusTypes = 'gained' | 'lost';
type SetupTypes = 'single policy' | 'all policies';
type SourceSystemTypes = 'EIS' | 'GWPC';

/**
 * Maps a policy's `dataSource` (from the welcome API policies list) to the
 * analytics `source_system` value: PLPC (Guidewire / nextgen) -> 'GWPC',
 * otherwise (PAS) -> 'EIS'. Returns undefined when the policy number isn't
 * present or can't be matched to a known policy.
 */
const getSourceSystem = (
  policies: CustomerPolicy[] | undefined,
  policyNumber?: string
): SourceSystemTypes | undefined => {
  if (!policyNumber) return undefined;
  const policy = policies?.find((p) => p.policyNumber === policyNumber);
  if (!policy) return undefined;
  return policy.dataSource === 'PLPC' ? 'GWPC' : 'EIS';
};

export interface TrackProps {
  event_type: EventTypes;
  location?: LocationTypes;
  action?: string;
  account_type?: AnalyticsAccountTypes;
  api_response?: string;
  auto_claim_driver?: string;
  link?: string;
  claim_email?: string;
  claim_number?: string;
  claim_phone_number?: string;
  claim_policy_number?: string;
  claim_type?: string;
  claim_status?: string;
  contacted_email?: string;
  contents?: string;
  communication_type?: string;
  dialed_number?: string;
  displayed_phone_number?: string;
  displayed_auto_discount?: string;
  displayed_property_discount?: string;
  document_name?: string;
  document_type?: string;
  documents?: string[];
  email?: string;
  error_code?: string;
  error_message?: string;
  evalue_status?: EvalueStatusTypes;
  faq_topic?: string;
  faq_answer?: string;
  field_name?: string;
  filter_name?: string;
  filtered_value?: string;
  flow?: string;
  input?: string;
  message_type?: MessageTypes;
  mobile_number?: string;
  method?: MethodTypes;
  payment_amount?: string;
  payment_selection?: string | number | AmountFormValues;
  payment_type?: PaymentTypes;
  policy_changed?: string;
  policy_number?: string;
  policy_state?: string;
  source_system?: SourceSystemTypes;
  policy_type?: string;
  preference_changed?: string;
  product_type?: string;
  reason?: string;
  selection?: SelectionTypes;
  setup_type?: SetupTypes;
  state?: string;
  status?: StatusTypes;
  status_changed?: string;
  submission_destination?: string;
  topic?: string;
  incident_date?: string;
  incident_city?: string;
  incident_state?: string;
  incident_vehicle_count?: number;
  claimant_name?: string;
  phone_number?: string;
  destination?: string;
  insured_first_name?: string;
  insured_last_name?: string;
  vehicle_information?: string;
  incident_injury?: string;
  incident_description?: string;
  incident_damage?: string;
  incident_cause?: string;
  incident_details?: string;
  autopay?: boolean;
  category?: string;
  channel?: Channel;
  channel_type?: string;
  transaction_type?: string;
  smartlink_phone_number?: string;
  smartlink_email?: string;
  uuid?: string;
  landing_page?: string;
  own_repair_shop?: string;
  recommendation_request?: string;
  repair_shop_details?: string;
  modal_name?: string;
  vendor?: string;
  questionnaire?: string;
  questionnaire_type?: string;
  message?: string;
  reporter_name?: string;
  relationship_to_insured?: string;
  preferred_contact_method?: string;
  property_address?: string;
  property_address_1?: string;
  property_address_2?: string;
  property_city?: string;
  property_state?: string;
  property_zip_code?: string;
  incident_occurrence_date?: string;
  incident_identified_date?: string;
  incident_type?: string;
  incident_3_room_damage_flag?: string;
  expert_assessment_completed?: string;
  expert_advisement?: string;
  have_own_contractor?: string;
  contractor_name?: string;
  contractor_phone_number?: string;
  claim_policy_state?: string;
  claim_product_type?: string;
  claim_incident_occurrence_date?: string;
  claim_incident_identified_date?: string;
  claim_incident_description?: string;
  claim_incident_type?: string;
  claim_incident_cause?: string;
  claim_incident_3_room_damage_flag?: string;
  claim_name?: string;
  claim_reporter_name?: string;
  claim_relationship_to_insured?: string;
  claim_preferred_contact_method?: string;
  claim_property_address?: string;
  claim_property_address_1?: string;
  claim_property_address_2?: string;
  claim_property_city?: string;
  claim_property_state?: string;
  claim_property_zip_code?: string;
  claim_expert_assessment_completed?: string;
  claim_expert_advisement?: string;
  claim_have_own_contractor?: string;
  claim_contractor_name?: string;
  claim_contractor_phone_number?: string;
}

export interface DirectAccessTrackProps extends TrackProps {
  club_code?: string;
  registration_type?: 'Organic' | 'OTVC' | 'Smartlink';
  flow?: 'Auto' | 'Property' | 'SMS' | 'Email' | 'Simplified';
  login_method?: 'DA' | 'SSO';
}

export type TrackFunction = (eventName: string, properties: TrackProps) => void;

const startCase = (text: string): string =>
  handleForwardSlash(text)
    .split(' ')
    .map((txt) => capitalize(txt))
    .join(' ');

export const usePageTrack = () => {
  const location = useLocation();
  const isMobileAppUser = useIsMobileAppUser();
  const channel = getChannel(isMobileAppUser);
  useEffect(() => {
    if (location.pathname) {
      window.analytics?.page(location.pathname, { channel });
    }
  }, [location, channel]);
};

export const useIdentify = () => {
  const { data } = useWelcomeQuery();
  const { firstName, lastName } = getCustomerName(data);
  const isMobileAppUser = useIsMobileAppUser();
  const channel = getChannel(isMobileAppUser);

  useEffect(() => {
    if (data) {
      window.analytics?.identify(data.mdmId, {
        name: `${firstName} ${lastName}`,
        email: data.userInfo.email,
        login_method: data.userInfo.loginType === 'cloud' ? 'DA' : 'SSO',
        club_code: getClubCode(data?.userInfo?.clubCode),
        channel,
      });
    }
  }, [data, channel]);
};

/**
 * To be used only for Direct Access or other unauthenticated pages
 */
export const useDirectAccessAnalytics = () => {
  const { pathname } = useLocation();
  const isMobileAppUser = useIsMobileAppUser();
  const channel = getChannel(isMobileAppUser);
  const track = (eventName: string, properties: DirectAccessTrackProps) => {
    const trackFunction = window.analytics?.track || console.warn;

    return trackFunction(eventName, {
      category: pathname,
      label: startCase(eventName),
      channel,
      ...properties,
    });
  };

  return {
    track: /localhost/.test(window.location.hostname) ? () => {} : track,
  };
};

const useAnalytics = () => {
  const { pathname } = useLocation();
  const { data } = useWelcomeQuery();
  const isMobileAppUser = useIsMobileAppUser();
  const channel = getChannel(isMobileAppUser);
  const track: TrackFunction = (eventName, properties) => {
    const trackFunction = window.analytics?.track || console.warn;

    // policy_number is a per-event field (no global default); each event that
    // needs it passes it explicitly.
    const sourceSystem = getSourceSystem(
      data?.policies,
      properties.policy_number
    );

    return trackFunction(startCase(eventName), {
      category: pathname,
      label: startCase(eventName),
      email: data?.userInfo?.email,
      channel,
      club_code: getClubCode(data?.userInfo?.clubCode),
      policy_type: properties.policy_type || data?.policies?.[0]?.policyType,
      state:
        properties.policy_state ||
        data?.policies?.[0]?.policyNumber.slice(0, 2),
      ...properties,
      ...(sourceSystem && { source_system: sourceSystem }),
    });
  };

  return {
    track: /localhost/.test(window.location.hostname) ? () => {} : track,
  };
};

export const initializeAnalytics = () => {
  if (import.meta.env.VITE_SEGMENT_WRITE_KEY) {
    window.analytics?.load &&
      analytics.load(import.meta.env.VITE_SEGMENT_WRITE_KEY);
  }
};

export default useAnalytics;
